import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config/api';

const Devices = () => {
  const { token } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchDevices();
    }
  }, [token]);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/devices`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDevices(Array.isArray(data) ? data : []);
      } else {
        setDevices([]);
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'online') return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (statusLower === 'offline') return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
  };

  const getDeviceTypeIcon = (type) => {
    const typeLower = type?.toLowerCase();
    if (typeLower?.includes('mobile') || typeLower?.includes('phone')) {
      return (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M7 2a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V4a2 2 0 00-2-2H7zm3 14a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
      );
    }
    if (typeLower?.includes('laptop')) {
      return (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
        </svg>
      );
    }
    // Default desktop icon
    return (
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cyber-dark flex items-center justify-center">
        <div className="text-white text-xl">Loading devices...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cyber-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Connected Devices</h1>
          <p className="text-gray-400 mt-1">Monitor and manage all devices running CyberGuard Agent</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <h3 className="text-sm text-gray-400 mb-1">Total Devices</h3>
            <p className="text-3xl font-bold text-white">{devices.length}</p>
          </div>
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <h3 className="text-sm text-gray-400 mb-1">Online</h3>
            <p className="text-3xl font-bold text-green-400">
              {devices.filter(d => d.status?.toLowerCase() === 'online').length}
            </p>
          </div>
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <h3 className="text-sm text-gray-400 mb-1">Offline</h3>
            <p className="text-3xl font-bold text-gray-400">
              {devices.filter(d => d.status?.toLowerCase() === 'offline').length}
            </p>
          </div>
        </div>

        {/* Devices List */}
        <div className="bg-cyber-card border border-gray-800 rounded-lg overflow-hidden">
          {devices.length === 0 ? (
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-800 rounded-full mb-4">
                <svg className="w-8 h-8 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">No Devices Connected</h3>
              <p className="text-gray-400 mb-6">Install the CyberGuard Agent on your devices to start monitoring.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-cyber-darker border-b border-gray-700">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-400">Device</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-400">Type</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-400">Operating System</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-400">Status</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-400">Last Scan</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-400">Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device) => (
                    <tr key={device.id} className="border-b border-gray-700 hover:bg-cyber-darker transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-cyber-darker rounded-lg flex items-center justify-center mr-3 text-cyber-cyan">
                            {getDeviceTypeIcon(device.device_type)}
                          </div>
                          <div>
                            <div className="text-white font-medium">{device.device_name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-300">
                        {device.device_type || 'Desktop'}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-300">
                        {device.os || 'Unknown'}
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 text-xs rounded-full border ${getStatusColor(device.status)}`}>
                          {device.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-400">
                        {device.last_scan 
                          ? new Date(device.last_scan).toLocaleString()
                          : 'Never'
                        }
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-400">
                        {device.created_at 
                          ? new Date(device.created_at).toLocaleDateString()
                          : 'Unknown'
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Devices;